// @ts-nocheck
/*
  <pb-eoy-sparkles> — the drifting sparkle layer of the WONDR EOY 2026 sections
  (sections/pb-eoy-giftbox-showcase.liquid, sections/pb-eoy-discovery.liquid).

  Put it as the first child of a positioned section: it fills the section
  (assets/pb-eoy-sparkles.css), sits behind the content and sizes the sparkles
  to its own box.

  Attributes:
    data-count         sparkles on desktop (>= 750px)
    data-count-mobile  sparkles below 750px
    data-animate       present = sparkles drift (off for prefers-reduced-motion)

  - Mixed Figma sizes; the biggest and the smallest are always in the mix.
  - Placement: each sparkle goes to the least-populated quadrant (ties random),
    then best-candidate sampling inside it, so they are random but balanced
    left/right and top/bottom.
  - Motion: each one wanders at its own speed with a randomly drifting heading,
    turns gently away from close neighbours and bounces off the edges. Runs
    only while the layer is on screen.
*/
(() => {
  if (customElements.get('pb-eoy-sparkles')) return;

  const SPARKLE_RATIO = 0.761; // width / height, from Figma
  // Sparkle widths from the Figma frames (px); height = width / SPARKLE_RATIO.
  const SPARKLE_WIDTHS = [21.12, 14.4, 26.88, 13.44, 17.28, 13.44, 23.04, 14.4, 19.2];
  const MAX_SPARKLES = 50;
  const CANDIDATES = 24; // random spots tried per sparkle; the emptiest wins
  const MOBILE_QUERY = '(max-width: 749px)';
  const EDGE = 4; // free space kept to the edges
  const SPEED = [5, 12]; // px per second
  const MAX_TURN = 0.6; // rad per second

  const SPARKLE_SVG =
    '<svg viewBox="0 0 21.12 27.7543" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M10.56 0L11.4975 6.68038C11.9234 9.7136 13.7283 12.0895 16.0365 12.6452L21.12 13.8771L16.0365 15.1091C13.7283 15.6689 11.9204 18.0407 11.4975 21.0739L10.56 27.7543L9.6225 21.0739C9.19656 18.0407 7.39169 15.6648 5.08353 15.1091L0 13.8771L5.08353 12.6452C7.39169 12.0854 9.19965 9.7136 9.6225 6.68038L10.56 0Z" fill="currentColor"/>' +
    '</svg>';

  const rand = (min, max) => min + Math.random() * (max - min);

  class PbEoySparkles extends HTMLElement {
    connectedCallback() {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.animated = this.hasAttribute('data-animate') && !reduced;

      // Desktop and mobile have their own count: rebuild when crossing 750px.
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.onBreakpoint = () => this.build();
      this.mobileQuery.addEventListener('change', this.onBreakpoint);

      // Section size changes (tab switch, unbox, resize, font swap) → keep them inside.
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      this.resizeObserver.observe(this);

      // Only move while on screen.
      if (this.animated) {
        this.visible = false;
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible) this.startMotion();
        });
        this.intersectionObserver.observe(this);
      }

      this.build();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.mobileQuery?.removeEventListener('change', this.onBreakpoint);
      cancelAnimationFrame(this.layoutFrame);
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = null;
    }

    // Always the biggest and the smallest, the rest shuffled; more than 9 repeat at random.
    widths(count) {
      const sorted = [...SPARKLE_WIDTHS].sort((a, b) => a - b);
      const rest = sorted.slice(1, -1).sort(() => Math.random() - 0.5);
      const widths = [sorted[sorted.length - 1], sorted[0], ...rest];
      while (widths.length < count) {
        widths.push(SPARKLE_WIDTHS[Math.floor(Math.random() * SPARKLE_WIDTHS.length)]);
      }
      return widths.slice(0, count);
    }

    build() {
      const raw = this.mobileQuery.matches ? this.dataset.countMobile : this.dataset.count;
      const count = Math.max(0, Math.min(MAX_SPARKLES, Number.parseInt(raw, 10) || 0));

      this.innerHTML = '';
      this.sparkles = this.widths(count).map((w) => {
        const el = document.createElement('span');
        el.className = 'pb-eoy-sparkles__sparkle';
        el.innerHTML = SPARKLE_SVG;
        el.style.width = `${w}px`;
        el.style.setProperty('--pb-eoy-sparkle-dur', `${rand(2.5, 5).toFixed(2)}s`);
        el.style.setProperty('--pb-eoy-sparkle-delay', `${(-rand(0, 5)).toFixed(2)}s`);
        el.style.setProperty('--pb-eoy-sparkle-o', rand(0.7, 0.95).toFixed(2));
        this.appendChild(el);
        return {
          el,
          w,
          h: w / SPARKLE_RATIO,
          x: 0,
          y: 0,
          placed: false,
          angle: rand(0, Math.PI * 2),
          turn: rand(-MAX_TURN, MAX_TURN),
          speed: rand(SPEED[0], SPEED[1]),
        };
      });

      this.scheduleLayout();
    }

    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => this.layout());
    }

    isFree(box) {
      const { width, height } = this.bounds;
      return box.x >= EDGE && box.y >= EDGE && box.x + box.w <= width - EDGE && box.y + box.h <= height - EDGE;
    }

    // Each sparkle goes to the quadrant with the fewest sparkles, then
    // best-candidate sampling inside it: try CANDIDATES random spots and keep
    // the one farthest from the sparkles already placed (and not hugging an
    // edge). Later passes (resize) keep every sparkle that still fits and only
    // re-place the ones now outside.
    layout() {
      if (!this.sparkles?.length) return;
      this.bounds = { width: this.clientWidth, height: this.clientHeight };
      const { width, height } = this.bounds;
      if (!width || !height) return;

      const placed = this.sparkles.filter((sparkle) => sparkle.placed && this.isFree(sparkle));
      const quadrantOf = (s) => (s.x + s.w / 2 >= width / 2 ? 1 : 0) + (s.y + s.h / 2 >= height / 2 ? 2 : 0);

      for (const sparkle of this.sparkles) {
        if (placed.includes(sparkle)) continue;

        const counts = [0, 0, 0, 0];
        for (const other of placed) counts[quadrantOf(other)] += 1;
        const fewest = Math.min(...counts);
        const options = [0, 1, 2, 3].filter((q) => counts[q] === fewest);
        const quadrant = options[Math.floor(Math.random() * options.length)];
        const right = quadrant % 2 === 1;
        const bottom = quadrant >= 2;
        const minX = right ? width / 2 : EDGE;
        const maxX = right ? width - sparkle.w - EDGE : width / 2 - sparkle.w;
        const minY = bottom ? height / 2 : EDGE;
        const maxY = bottom ? height - sparkle.h - EDGE : height / 2 - sparkle.h;

        let best = null;
        let bestScore = -1;
        for (let i = 0; i < CANDIDATES; i++) {
          const x = rand(minX, Math.max(minX, maxX));
          const y = rand(minY, Math.max(minY, maxY));
          const cx = x + sparkle.w / 2;
          const cy = y + sparkle.h / 2;
          // Distance to the nearest sparkle, or to the nearest edge counted double.
          let score = Math.min(cx, cy, width - cx, height - cy) * 2;
          for (const other of placed) {
            score = Math.min(score, Math.hypot(cx - other.x - other.w / 2, cy - other.y - other.h / 2));
          }
          if (score > bestScore) {
            bestScore = score;
            best = { x, y };
          }
        }

        sparkle.x = best.x;
        sparkle.y = best.y;
        sparkle.placed = true;
        sparkle.el.classList.add('is-placed');
        placed.push(sparkle);
        this.paint(sparkle);
      }

      if (this.animated) this.startMotion();
    }

    paint(sparkle) {
      sparkle.el.style.transform = `translate3d(${sparkle.x.toFixed(2)}px, ${sparkle.y.toFixed(2)}px, 0)`;
    }

    startMotion() {
      if (this.motionFrame || !this.visible || !this.isConnected) return;

      let last = performance.now();
      const tick = (now) => {
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        for (const sparkle of this.sparkles) {
          if (sparkle.placed) this.step(sparkle, dt);
        }
        this.motionFrame = this.visible ? requestAnimationFrame(tick) : null;
      };
      this.motionFrame = requestAnimationFrame(tick);
    }

    // Wander: the heading drifts smoothly at random; when the next move would
    // leave the layer, the sparkle bounces off the edge instead.
    step(sparkle, dt) {
      sparkle.turn = Math.max(-MAX_TURN, Math.min(MAX_TURN, sparkle.turn + rand(-1, 1) * 1.2 * dt));
      sparkle.angle += sparkle.turn * dt;

      // Keep the spread while wandering: turn gently away from close neighbours.
      const { width, height } = this.bounds;
      const radius = Math.sqrt((width * height) / this.sparkles.length) * 0.6;
      const cx = sparkle.x + sparkle.w / 2;
      const cy = sparkle.y + sparkle.h / 2;
      let pushX = 0;
      let pushY = 0;
      for (const other of this.sparkles) {
        if (other === sparkle || !other.placed) continue;
        const dx = cx - (other.x + other.w / 2);
        const dy = cy - (other.y + other.h / 2);
        const distance = Math.hypot(dx, dy);
        if (distance > 0 && distance < radius) {
          pushX += (dx / distance) * (1 - distance / radius);
          pushY += (dy / distance) * (1 - distance / radius);
        }
      }
      if (pushX || pushY) {
        const away = Math.atan2(pushY, pushX);
        const diff = Math.atan2(Math.sin(away - sparkle.angle), Math.cos(away - sparkle.angle));
        sparkle.angle += diff * Math.min(1, Math.hypot(pushX, pushY)) * 1.5 * dt;
      }

      const candidates = [sparkle.angle, Math.PI - sparkle.angle, -sparkle.angle, sparkle.angle + Math.PI];
      for (const angle of candidates) {
        const next = {
          x: sparkle.x + Math.cos(angle) * sparkle.speed * dt,
          y: sparkle.y + Math.sin(angle) * sparkle.speed * dt,
          w: sparkle.w,
          h: sparkle.h,
        };
        if (!this.isFree(next)) continue;

        sparkle.angle = angle;
        sparkle.x = next.x;
        sparkle.y = next.y;
        this.paint(sparkle);
        return;
      }

      // Outside the layer (it shrank under it): the layout pass re-places it.
      this.scheduleLayout();
    }
  }

  customElements.define('pb-eoy-sparkles', PbEoySparkles);
})();
